import { Channel } from './channel.model';

/**
 * Channel health — client mirror of the API contract (SPEC-2026-08-channel-health).
 *
 * The API serves these raw (ADR-002 precedent) and never includes channel credentials
 * (RNF-03), so this mirror carries only presentation-safe fields.
 */

export enum ChannelHealthClassificationEnum {
  HEALTHY = 'HEALTHY',
  ATTENTION = 'ATTENTION',
  CRITICAL = 'CRITICAL',
  NO_DATA = 'NO_DATA',
}

export enum ChannelEventKindEnum {
  STATUS_CHANGE = 'STATUS_CHANGE',
  QUALITY_CHANGE = 'QUALITY_CHANGE',
  LIMIT_CHANGE = 'LIMIT_CHANGE',
  ACCOUNT_UPDATE = 'ACCOUNT_UPDATE',
  ACCOUNT_REVIEW = 'ACCOUNT_REVIEW',
  TEMPLATE_QUALITY = 'TEMPLATE_QUALITY',
  BILLING = 'BILLING',
  OPT_OUT = 'OPT_OUT',
  ACCESS_LOST = 'ACCESS_LOST',
  NAME_UPDATE = 'NAME_UPDATE',
}

export enum ChannelEventSeverityEnum {
  INFO = 'INFO',
  WARN = 'WARN',
  CRITICAL = 'CRITICAL',
}

export interface ChannelHealthEntity {
  type: 'PHONE_NUMBER' | 'WABA' | 'BUSINESS' | 'APP' | 'MESSAGE_TEMPLATE';
  id?: string;
  canSendMessage: string;
  reason?: string;
  /** Meta's error_code behind `reason` — lets the UI translate known reasons. */
  reasonCode?: number;
}

export interface ChannelHealthPendingIssue {
  code: string;
  category: string;
  title: string;
  link?: string;
  /** Meta's error_code when the title is provider wording. */
  reasonCode?: number;
}

export interface ChannelHealthSignals {
  qualityRating?: string;
  numberStatus?: string;
  /** The display name as Meta has it (the platform's channel.name is internal). */
  verifiedName?: string;
  nameStatus?: string;
  newNameStatus?: string;
  messagingLimit?: string;
  throughputLevel?: string;
  codeVerificationStatus?: string;
  businessVerificationStatus?: string;
  accountReviewStatus?: string;
  obaStatus?: string;
  billingBlocked?: boolean;
  lastQualityChangeAt?: Date | string;
}

export interface ChannelHealth {
  classification: ChannelHealthClassificationEnum;
  /** Provider aggregate: AVAILABLE | LIMITED | BLOCKED. */
  canSendMessage?: string;
  entities?: Array<ChannelHealthEntity>;
  mainPendingIssue?: ChannelHealthPendingIssue;
  signals?: ChannelHealthSignals;
  appMode?: string;
  readAt: Date | string;
}

export interface ChannelEvent {
  _id: string;
  channel: string | Channel;
  company: string;
  kind: ChannelEventKindEnum | string;
  severity: ChannelEventSeverityEnum | string;
  title: string;
  detail?: string;
  source: 'WEBHOOK' | 'DIFF' | string;
  createdAt: Date | string;
}

// ---------------------------------------------------------------- endpoint payloads

export interface ChannelHealthListResponse {
  channels: Array<Channel>;
  counts: { total: number; critical: number; attention: number; healthy: number; noData: number };
  lastSyncAt?: Date | string;
}

export interface ChannelHealthDetailResponse {
  channel: Channel;
  pendingIssues: Array<ChannelHealthPendingIssue>;
  events: Array<ChannelEvent>;
  /** Hybrid channels only: the session side (SPEC-2026-10-whatsapp-hybrid-channel RF-22). */
  session?: ChannelHealthHybridSession;
}

export interface ChannelHealthHybridSession {
  paired: boolean;
  connected: boolean;
  lastSeenAt?: Date | string;
  mismatch: boolean;
}

export interface ChannelHealthRefreshResponse {
  channel: Channel;
}

export interface ChannelHealthCompanyRefreshResponse {
  ok: number;
  failed: number;
  skipped: number;
}

export interface ChannelQualityResponse {
  quality: { band?: string; lastChangeAt?: Date | string };
  kpis: {
    deliveryRate: number | null;
    readRate: number | null;
    sent30d: number;
    billedVolume30d: number | null;
    billedCost30d: number | null;
  };
  series: Array<{ day: string; sent: number; delivered: number }>;
  templates: Array<{ id: string; name: string; category: string; quality: string; sent: number; readRate: number | null }>;
  optOuts: { last30: number; delta: number };
}

export interface ChannelSummaryResponse {
  banner: {
    classification: ChannelHealthClassificationEnum;
    canSendMessage?: string;
    mainPendingIssue?: ChannelHealthPendingIssue;
  };
  metaInfo: {
    readAt?: Date | string;
    numberStatus?: string;
    qualityRating?: string;
    messagingLimit?: string;
    verifiedName?: string;
    nameStatus?: string;
    newNameStatus?: string;
    displayPhoneNumber?: string;
    countryCode?: string;
  };
  usage: { messages30d: number; attendances30d: number; activeContacts: number };
  series: Array<{ day: string; sent: number; delivered: number }>;
  connection: {
    connected: boolean;
    type: string;
    mode?: string;
    throughputLevel?: string;
    messagesPerSecond?: number;
    connectedSince?: Date | string;
  };
}

/** OK · WARNING from 80% of the allowance · EXHAUSTED once every free message was used. */
export type ChannelServiceAllowanceStatus = 'OK' | 'WARNING' | 'EXHAUSTED';

export interface ChannelMetaUsageCategory {
  /** Meta pricing category (MARKETING, UTILITY, AUTHENTICATION, …). */
  category: string;
  /** Delivered messages of the category this month, every pricing type. */
  volume: number;
  /** Messages Meta charged (pricing type REGULAR). */
  billableVolume: number;
  /** Meta's charge in the WABA currency; null when Meta returns no cost. */
  cost: number | null;
}

/** Sum of every category of the month so far — what Meta charged this number since day 1. */
export interface ChannelMetaUsageMonthTotal {
  /** Delivered messages, every category and pricing type (free ones included). */
  volume: number;
  /** Messages Meta charged (pricing type REGULAR). */
  billableVolume: number;
  /** Total spend in the WABA currency; null when Meta returns no cost. */
  cost: number | null;
}

/**
 * Meta usage of the month (`GET /channel/meta-usage/:idChannel`) — how much of Meta's monthly
 * allowance of free service messages the channel's number has used (charged from the first
 * message beyond it), plus the month's spend per category and its total so far.
 */
/**
 * The month's service messages priced at the client's rate (BRL) — sent only when Meta tells no
 * spend: `paid = total − freeCustomerService − freeEntryPoint`, `amount = paid × unitPrice`.
 */
export interface ChannelServiceChargeEstimate {
  total: number;
  /** Free under the monthly allowance. */
  freeCustomerService: number;
  /** Free inside a Click to WhatsApp / Page CTA window (0 on the platform estimate). */
  freeEntryPoint: number;
  paid: number;
  unitPrice: number;
  /** WALLET = the company's WhatsApp wallet price; DEFAULT = the standard table. */
  unitPriceSource: 'WALLET' | 'DEFAULT';
  amount: number;
  currency: 'BRL';
}

/** Meta's error on the pricing analytics read — full failure (PLATFORM) or cost refused (META). */
export interface ChannelMetaUsageError {
  reason: 'PROVIDER_ERROR' | 'MISSING_FIELD' | 'NO_CATEGORY' | 'EXCEPTION';
  httpStatus?: number | null;
  /** Graph error code (190 token, 200/10 permission, 100 parameter, 4/80008 rate limit…). */
  code?: number | string | null;
  subcode?: number | null;
  type?: string | null;
  message?: string | null;
  fbtraceId?: string | null;
  /** Metrics asked in the read that failed (VOLUME, COST — or VOLUME on the retry). */
  metricTypes?: Array<string>;
  /** Top-level keys Graph answered with when the field was missing. */
  responseKeys?: Array<string>;
  /** The error that led to this read — the cost refusal when the volume-only retry failed too. */
  cause?: ChannelMetaUsageError | null;
}

export interface ChannelMetaUsageResponse {
  /** META = Meta's pricing analytics; PLATFORM = the platform's own delivery records (estimate). */
  source: 'META' | 'PLATFORM';
  /** PHONE_NUMBER = this channel's number; WABA = every number of the account (no identifier). */
  scope: 'PHONE_NUMBER' | 'WABA';
  period: { start: Date | string; end: Date | string; resetsAt: Date | string; timeZone: string };
  /** WABA billing currency (USD, BRL, …) — every cost is in it. */
  currency: string | null;
  serviceAllowance: {
    limit: number;
    used: number;
    remaining: number;
    exceeded: number;
    usageRatio: number;
    status: ChannelServiceAllowanceStatus;
  };
  service: {
    volume: number;
    /** Free entry point window (Click to WhatsApp ads, Page CTA — up to 7 days): free, outside the allowance. */
    freeEntryPointVolume: number;
    /** Free under the monthly allowance — null on the platform estimate. */
    freeCustomerServiceVolume?: number | null;
    billableVolume: number | null;
    cost: number | null;
  };
  /** Every category of the month, service first — only filled when the source is META. */
  categories: Array<ChannelMetaUsageCategory>;
  /** The month's total so far across every category — null when the source is PLATFORM. */
  monthTotal: ChannelMetaUsageMonthTotal | null;
  /** Service messages priced at the client's rate — only when Meta tells no spend. */
  serviceEstimate?: ChannelServiceChargeEstimate | null;
  /** Real-time count from the platform: delivered non-template messages of the month. */
  platformEstimate: { serviceDelivered: number };
  /** Meta's error: with PLATFORM, why Meta could not be read; with META, the cost refused (partner billing) while volumes came. */
  metaError?: ChannelMetaUsageError | null;
  fetchedAt: Date | string;
}

/** Business profile as Meta answers it (`GET .../whatsapp_business_profile`). */
export interface ChannelBusinessProfileResponse {
  data?: Array<{
    about?: string;
    address?: string;
    description?: string;
    email?: string;
    profile_picture_url?: string;
    websites?: Array<string>;
    vertical?: string;
    messaging_product?: string;
  }>;
}

export interface UpdateChannelProfileRequest {
  about?: string;
  description?: string;
  address?: string;
  email?: string;
  websites?: Array<string>;
  vertical?: string;
}
